/**
 * Filter eligible users based on job category, role, property area, and service type
 * @param {Array} users - Array of user objects
 * @param {string} category - Job category (CLEANING, WALKTHROUGH, etc.)
 * @param {Object} selectedProperty - Selected property with area information
 * @param {Object} selectedServiceType - Selected service type with allowed roles
 * @param {boolean} includeAdmins - Whether to include admin roles (for custom jobs)
 * @returns {Array} Filtered array of eligible users
 */
export const getEligibleUsers = (users = [], category, selectedProperty = null, selectedServiceType = null, includeAdmins = true) => {
  if (!users || !Array.isArray(users)) return [];

  return users.filter(user => {
    // Filter out inactive users
    if (user.isActive === false) return false;
    
    // 1. AREA-BASED FILTERING
    // If property is selected and has an area, only show users assigned to that area
    if (selectedProperty?.area && user.areas && Array.isArray(user.areas)) {
      const propertyArea = selectedProperty.area.toUpperCase();
      const userAreas = user.areas.map(area => area.toUpperCase());
      
      // User must be assigned to the property's area
      if (!userAreas.includes(propertyArea)) {
        return false;
      }
    }
    
    // 2. ADMIN ROLE INCLUSION (for custom jobs)
    // For custom jobs, allow admin roles if they pass area filtering
    const adminRoles = ['ADMIN', 'GUEST_APP_MANAGER'];
    if (includeAdmins && adminRoles.includes(user.role)) {
      return true; // Admin passed area filtering, include them
    }
    
    // 3. SERVICE TYPE ROLE FILTERING (most specific)
    // If service type is selected and has allowed roles, use those
    if (selectedServiceType?.allowedRoles && Array.isArray(selectedServiceType.allowedRoles)) {
      return selectedServiceType.allowedRoles.includes(user.role);
    }
    
    // 4. FALLBACK TO CATEGORY-BASED FILTERING
    // If no service type roles defined, use category-based filtering
    switch (category) {
      case 'WALKTHROUGH':
        return user.role === 'PROPERTY_MANAGER';
      case 'CLEANING':
        return ['HOUSEKEEPER', 'HOUSEKEEPER_MANAGER'].includes(user.role);
      case 'MAINTENANCE':
        return user.role === 'MAINTENANCE';
      case 'INSPECTION':
        return ['PROPERTY_MANAGER', 'HOUSEKEEPER_MANAGER', 'INSPECTOR'].includes(user.role);
      default:
        return true;
    }
  }).map(user => {
    // Ensure user has a name field
    if (user.name) {
      return user;
    }
    return {
      ...user,
      name: `${user.firstName || ''} ${user.lastName || ''}`.trim()
    };
  });
};

/**
 * Get area display name from area enum value
 * @param {string} area - Area enum value
 * @returns {string} Formatted area name
 */
export const getAreaDisplayName = (area) => {
  const areaMap = {
    'KIAWAH': 'Kiawah',
    'ISLE_OF_PALMS': 'Isle of Palms',
    'ISLE_OF_PALMS_WILD_DUNES': 'Isle of Palms - Wild Dunes',
    'SEABROOK_ISLAND': 'Seabrook Island'
  };
  
  return areaMap[area] || area;
};

/**
 * Check if user can see financial fields
 * @param {Object} user - Current user object
 * @param {string} serviceTypeId - Selected service type ID
 * @param {Array} serviceTypes - Array of service types
 * @returns {boolean} Whether financial fields should be visible
 */
export const canSeeFinancialFields = (user, serviceTypeId, serviceTypes) => {
  if (!user?.role) return false;
  
  // Only admins and housekeeper managers can see financial fields
  if (!['ADMIN', 'GUEST_APP_MANAGER', 'HOUSEKEEPER_MANAGER'].includes(user.role)) return false;
  
  // Check if the selected service type allows financial fields
  if (serviceTypeId && serviceTypes) {
    const selectedServiceType = serviceTypes.find(type => type.id === serviceTypeId);
    return selectedServiceType?.showFinancialFields || false;
  }
  
  return false;
};

/**
 * Create job input object for API submission
 * @param {Object} formData - Form data object
 * @returns {Object} Formatted job input for API
 */
export const createJobInput = (formData) => {
  const {
    propertyId,
    category,
    scheduledDate,
    startTime,
    notes,
    serviceTypeId,
    assignedToId,
    payout,
    billedAmount
  } = formData;

  return {
    propertyId,
    category,
    scheduledDate,
    startTime,
    notes: notes || undefined,
    serviceTypeId: serviceTypeId || undefined,
    assignedToId: assignedToId ? parseInt(assignedToId, 10) : undefined,
    payout: payout ? parseFloat(payout) : undefined,
    billedAmount: billedAmount ? parseFloat(billedAmount) : undefined
  };
};

/**
 * Create recurring job input object for API submission
 * @param {Object} formData - Form data object
 * @returns {Object} Formatted recurring job input for API
 */
export const createRecurringJobInput = (formData, selectedProperty) => {
  const {
    propertyId,
    category,
    frequency,
    startTime,
    scheduledDate,
    endDate,
    notes,
    serviceTypeId,
    assignedToId,
    dayOfWeek,
    dayOfMonth,
    weekOfMonth
  } = formData;

  // Auto-generate name from property name and frequency
  const propertyName = selectedProperty?.name || 'Property';
  const frequencyText = frequency.toLowerCase().replace('_', ' ');
  const autoGeneratedName = `${propertyName} - ${frequencyText} ${category.toLowerCase()}`;

  const input = {
    name: autoGeneratedName,
    propertyId,
    category,
    frequency,
    startTime,
    endDate: new Date(endDate).toISOString(),
    notes: notes || undefined,
    serviceTypeId: serviceTypeId || undefined,
    assignedToId: assignedToId ? parseInt(assignedToId, 10) : undefined
  };

  // Add frequency-specific fields
  if (frequency === 'WEEKLY' || frequency === 'BIWEEKLY') {
    input.dayOfWeek = parseInt(dayOfWeek, 10);
    // For weekly/biweekly jobs, use the scheduled date as start date
    input.startDate = scheduledDate ? new Date(scheduledDate).toISOString() : undefined;
  } else if (frequency === 'MONTHLY') {
    input.dayOfMonth = parseInt(dayOfMonth, 10);
    // For monthly jobs, use the scheduled date as start date
    input.startDate = scheduledDate ? new Date(scheduledDate).toISOString() : undefined;
  } else if (frequency === 'QUARTERLY') {
    input.dayOfMonth = parseInt(dayOfMonth, 10);
    // For quarterly jobs, use the scheduled date as start date
    input.startDate = scheduledDate ? new Date(scheduledDate).toISOString() : undefined;
  } else {
    // For daily jobs, use the scheduled date as start date
    input.startDate = scheduledDate ? new Date(scheduledDate).toISOString() : undefined;
  }

  return input;
};

/**
 * Get category from service type
 * @param {string} serviceTypeId - Service type ID
 * @param {Array} serviceTypes - Array of service types
 * @returns {string} Job category
 */
export const getCategoryFromServiceType = (serviceTypeId, serviceTypes) => {
  if (!serviceTypeId || !serviceTypes) return 'CLEANING';
  
  const selectedServiceType = serviceTypes.find(type => type.id === serviceTypeId);
  return selectedServiceType?.category || 'CLEANING';
};

/**
 * Get service type object from service type ID
 * @param {string} serviceTypeId - Service type ID
 * @param {Array} serviceTypes - Array of service types
 * @returns {Object|null} Service type object or null
 */
export const getServiceTypeById = (serviceTypeId, serviceTypes) => {
  if (!serviceTypeId || !serviceTypes) return null;
  
  return serviceTypes.find(type => type.id === serviceTypeId) || null;
};

/**
 * Create manual job data for fallback scenarios
 * @param {Object} formData - Form data object
 * @param {string} jobId - Generated job ID
 * @param {Object} selectedProperty - Selected property object
 * @returns {Object} Manual job data object
 */
export const createManualJobData = (formData, jobId, selectedProperty) => {
  return {
    id: jobId,
    propertyId: formData.propertyId,
    category: formData.category,
    status: 'PENDING',
    scheduledDate: new Date(formData.scheduledDate),
    startTime: formData.startTime,
    notes: formData.notes,
    property: {
      id: formData.propertyId,
      name: selectedProperty?.name || 'Unknown Property',
      address: selectedProperty?.address || '',
      propertyCode: selectedProperty?.propertyCode || '',
      checkOutTime: selectedProperty?.checkOutTime || '10:00'
    }
  };
}; 